/**
 * Documents tab (§2.4, screenshots/04): "Meeting files" on the left (selected file highlighted, a download
 * button per file, the archive source note), and a preview of the selected file on the right with its
 * name, "Page 1 of 14", zoom out / zoom in / open / Download. PDFs open in the PdfViewer (components/documents:
 * screen-tall with its own scroll, fitted to the width, full screen, and annotations shared with the team); TXT and
 * VTT files show as text on the page.
 */
import { List, ListRow, RowActions, RowLead, RowMain, RowSub, RowTitle } from "../shared/List";
import { useEffect, useState } from "react";
import { useStore } from "zustand";
import { Captions, Download, ExternalLink, FileText, ScrollText, ZoomIn, ZoomOut } from "lucide-react";
import { useMeeting } from "../../stores/meetingStore";
import type { MeetingDocument } from "../../types";
import { EmptyState } from "../shared/States";
import { PdfViewer } from "../documents/PdfViewer";
import { entitlementsStore } from "../../realtime/entitlements";
import { can } from "../../nav/navConfig";

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const PAGE_WIDTH = 500; // a text file's page at 100% (reference: Page 1, w-500)
const ICON = { transcript: ScrollText, captions: Captions } as Record<string, typeof FileText>;

const formatOf = (d: MeetingDocument) => d.format ?? d.url.split(/[?#]/)[0].split(".").pop()?.toLowerCase() ?? "pdf";
const TEXT_FORMATS = new Set(["txt", "vtt"]);
const titleOf = (d: MeetingDocument) => d.title ?? d.kind.charAt(0).toUpperCase() + d.kind.slice(1);
const fileName = (d: MeetingDocument) => `${titleOf(d)}.${formatOf(d)}`;
const metaOf = (d: MeetingDocument) => [
  formatOf(d)?.toUpperCase(),
  d.pages != null ? `${d.pages} ${d.pages === 1 ? "page" : "pages"}` : d.words != null ? `${d.words.toLocaleString("en-US")} words` : null,
  d.source,
].filter(Boolean).join(" · ");

function TextPreview({ url, zoom }: { url: string; zoom: number }) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    const ac = new AbortController();
    setText(null);
    fetch(url, { signal: ac.signal }).then((r) => (r.ok ? r.text() : Promise.reject())).then(setText).catch(() => !ac.signal.aborted && setText(""));
    return () => ac.abort();
  }, [url]);
  return (
    <div className="doc-page doc-page--text" style={{ width: PAGE_WIDTH * zoom, fontSize: 11 * zoom }}>
      {text === null ? null : text ? <pre>{text}</pre> : <p className="doc-error">This file can't be previewed. Download it to read it.</p>}
    </div>
  );
}

export function DocumentsTab() {
  const documents = useMeeting((s) => s.meeting?.documents ?? []);
  const sourceUrl = useMeeting((s) => s.meeting?.documentsSourceUrl);
  // Chicago's files are archived copies of the City Clerk's; another city's come from its Legistar (documentsSourceName).
  const sourceName = useMeeting((s) => s.meeting?.documentsSourceName) ?? "City Clerk";
  const [selected, setSelected] = useState(0);
  const [zoom, setZoom] = useState(2); // index into ZOOMS (100%), text files
  const doc = documents[selected];
  // Your annotations are shared with your team when it has shared notes (worker/src/annotations.js).
  const shared = useStore(entitlementsStore, (s) => !!s.group && can(s.group, s.role, "sharedNotes"));
  // Never blank (screenshots/54): no files yet → a state card that points to the source.
  if (!doc) return <div className="tab-panel"><EmptyState icon="info" title="No meeting files yet" body=""
    action={sourceUrl ? { label: `Open on ${sourceName}`, icon: "external-link", href: sourceUrl } : undefined} /></div>;
  const isPdf = formatOf(doc) === "pdf";

  return (
    <div className="documents-tab tab-panel" role="tabpanel">
      <section className="files">
        <h2 className="files-title">Meeting files</h2>
        <div className="files-gap" />
        <List variant="plain">
          {documents.map((d, i) => {
            const Icon = ICON[d.kind] ?? (formatOf(d) === "vtt" ? Captions : FileText);
            return (
              <ListRow key={d.url} className="file-row" current={i === selected} onOpen={() => setSelected(i)} wrap={false}>
                <RowLead className="file-icon"><Icon size={17} /></RowLead>
                <RowMain min={0}>
                  <RowTitle className="file-name">{titleOf(d)}</RowTitle>
                  <RowSub className="file-meta">{metaOf(d)}</RowSub>
                </RowMain>
                <RowActions>
                  <a className="icon-btn file-download" href={d.url} download={fileName(d)} aria-label={`Download ${titleOf(d)}`}>
                    <Download size={16} />
                  </a>
                </RowActions>
              </ListRow>
            );
          })}
        </List>
        {sourceUrl && (
          <a className="source-note" href={sourceUrl} target="_blank" rel="noreferrer">{sourceName === "City Clerk" ? "Copies archived from the City Clerk's site" : `The meeting's record on ${sourceName}`}<ExternalLink size={12} /></a>
        )}
      </section>

      {isPdf ? <PdfViewer key={doc.url} url={doc.url} fileName={fileName(doc)} shared={shared} /> : (
        <section className="doc-preview doc-viewer" aria-label={`Preview of ${fileName(doc)}`}>
          <div className="doc-toolbar">
            <span className="doc-name-group"><span className="doc-name">{fileName(doc)}</span></span>
            <span className="doc-tools">
              <button className="icon-btn" aria-label="Zoom out" disabled={zoom === 0} onClick={() => setZoom(zoom - 1)}><ZoomOut size={15} /></button>
              <button className="icon-btn" aria-label="Zoom in" disabled={zoom === ZOOMS.length - 1} onClick={() => setZoom(zoom + 1)}><ZoomIn size={15} /></button>
              <a className="icon-btn" href={doc.url} target="_blank" rel="noreferrer" aria-label="Open in a new tab"><ExternalLink size={15} /></a>
              <a className="btn btn--primary doc-download" href={doc.url} download={fileName(doc)}><Download size={14} />Download</a>
            </span>
          </div>
          <div className="doc-body">
            <div className="doc-pages" tabIndex={0} role="region" aria-label={`${fileName(doc)}, pages`}>
              {TEXT_FORMATS.has(formatOf(doc))
                ? <TextPreview url={doc.url} zoom={ZOOMS[zoom]} />
                : <p className="doc-error">This file can't be previewed. Download it to read it.</p>}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
